import {css, html, LitElement} from "lit";
import {mix} from "mixwith";
import SelectionMixin from "./Mixins/SelectionMixin.js";
import {styleMap} from "lit/directives/style-map.js";
import {classMap} from "lit/directives/class-map.js";
import {renderFrame} from "../Utils/LitUtils.js";

/**
 * TODO: Scrolling
 */
export class AuiTabs extends mix(LitElement).with(SelectionMixin) {

	static get properties() {
		return {
			disableAnimation: Boolean,

			// Private
			__tabWidth: { type: Number, attribute: false }
		};
	}

	get items() {
		return this.tabs;
	}

	set selected(value) {
		if(this.items.length === 0) {
			// No items available to be selected, store the value temporarily until items are available.
			this._selected = value;
		} else {
			super.selected = value;
		}
	}

	get selected() {
		return super.selected;
	}

	constructor() {
		super();
		this._resizeObserer = new ResizeObserver(this.onSizeChanged.bind(this));

		/**
		 * Currently available tabs (elements) in the slot.
		 *
		 * @type {Element[]}
		 */
		this.tabs = [];

		/**
		 * The active/selected index.
		 *
		 * @type {number}
		 */
		this.selected = 0;

		/**
		 * Disables the animation.
		 *
		 * @type {boolean}
		 */
		this.disableAnimation = false;
	}

	/**
	 * A wrapper to temporarily disable animation for e.g. selecting a page.
	 *
	 * @param {function} callback
	 */
	async withoutAnimation(callback) {
		const prev = this.disableAnimation;

		this.disableAnimation = true;

		await renderFrame();
		callback();
		await renderFrame();

		this.disableAnimation = prev;

		return renderFrame();
	}

	update(changedProperties) {
		if(changedProperties.has("selected"))
			this.updateActiveTab(changedProperties.get("selected"));

		super.update(changedProperties);
	}

	updateActiveTab(prevSelected = undefined) {
		if(prevSelected) {
			this.deactivateTab(this.tabs[prevSelected]);
			this.activateTab(this.tabs[this.selected]);
		} else {
			// The tabs probably changes, update all
			this.tabs.forEach((tab, index) => {
				if(index === this.selected) {
					this.activateTab(tab);
				} else
					this.deactivateTab(tab);
			});
		}
	}

	activateTab(tab) {
		tab.setAttribute("active", "");
		if(this._resizeObserer)
			this._resizeObserer.observe(tab);
	}

	deactivateTab(tab) {
		if(this._resizeObserer)
			this._resizeObserer.unobserve(tab);
		tab.removeAttribute("active");
	}

	onSlotClick(e) {
		let index;

		/**
		 * Checks if a node is a slotted element.
		 *
		 * @param {Node} node
		 * @return {boolean}
		 */
		const isTab = node => this.tabs.some(tab => tab === node);

		// Use e.target as tab or search for it in the composedPath
		const clickedTab = e.target === isTab(e.target) ? e.target : e.composedPath().find(isTab);
		if(clickedTab) {
			const tabIndex = this.tabs.findIndex(tab => tab === clickedTab);
			if(tabIndex > -1)
				this.selected = tabIndex;
		}
	}

	onSlotMutate(e) {
		this.tabs = e.target.assignedElements();
		if(this._selected !== undefined) {
			// selected got set before items were available, set it now.
			this.withoutAnimation(() => this.selected = this._selected).then(() => {
				delete this._selected;
			});
		} else
			this.updateActiveTab();
	}

	onSizeChanged(entries) {
		this.__tabWidth = (entries[0].borderBoxSize[0] || entries[0].borderBoxSize).inlineSize;
	}

	static get styles() {
		return css`
			:host {
				--aui-tabs-animation-duration: 0.4s;
				--aui-tabs-bar-color: var(--theme-primary);
				
				display: block;
			}
			
			.container {
				overflow: hidden;
			}
			
			.view {
				display: flex;
			}
			
			.barContainer {
				position: relative;
				background: floralwhite;
				height: 2px;
			}
			
			.bar {
				background: var(--aui-tabs-bar-color);
				position: absolute;
				left: 0;
				top: 0;
				height: 100%;
			}
			
			.bar.animate {
				transition-property: width, transform;
				transition-duration: var(--aui-tabs-animation-duration);
				
				will-change: width, transform;
			}
			
			::slotted(*) {
				flex: 1;
				
				box-sizing: border-box;
				background: #DDD;
				display: flex;
				align-items: center;
				justify-content: center;
				padding: 8px;
				text-align: center;
				cursor: pointer;
			}
			
			.animate ::slotted(*) {
				transition-property: background-color, color;
				transition-duration: var(--aui-tabs-animation-duration);
			}
			
			::slotted([active]) {
				background: #FFF;
				color: var(--primary-color);
			}
			
			::slotted(:hover) {
				background: #EEE;
			}
		`;
	}

	get BarContainer() {
		if(!this._barContainer)
			this._barContainer = this.shadowRoot.querySelector(".barContainer");
		return this._barContainer;
	}

	getViewClassMap() {
		return {
			animate: !this.disableAnimation
		};
	}

	getBarStyle() {
		const containerX = this.BarContainer ? this.BarContainer.getBoundingClientRect().left : 0;
		const tabX = this.tabs[this.selected] ? this.tabs[this.selected].getBoundingClientRect().left : 0;
		const offsetX = containerX - tabX;
		const width = this.__tabWidth ? this.__tabWidth + "px" : "";

		return {
			width: width,
			transform: "translateX(" + -offsetX + "px)"
		};
	}

	getBarClassMap() {
		return {
			animate: !this.disableAnimation
		};
	}

	render() {
		return html`
            <div class="container">
                <div class="view ${classMap(this.getViewClassMap())}">
                    <slot @slotchange=${this.onSlotMutate} @click=${this.onSlotClick}></slot>
                </div>
                ${this.renderSelectionBar()}
            </div>
		`;
	}

	renderSelectionBar() {
		return html`
            <div id="BarContainer" class="barContainer">
                <div class="bar ${classMap(this.getBarClassMap())}" style=${styleMap(this.getBarStyle())}></div>
            </div>
		`;
	}

}

customElements.define("aui-tabs", AuiTabs);

export default AuiTabs;